import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import grapesjs from 'grapesjs';
import 'grapesjs/dist/css/grapes.min.css';
import 'grapesjs-preset-webpage';
import { ArrowLeft, Save, Globe, Eye, Monitor, Smartphone, Code } from 'lucide-react';
import api from '../../services/api';
import { useToast } from '../../context/ToastContext';

export default function AdminTemplateEditor() {
    const { id } = useParams(); // ID if editing existing template
    const navigate = useNavigate();
    const toast = useToast();
    const editorRef = useRef(null);
    const [editor, setEditor] = useState(null);
    const [templateData, setTemplateData] = useState({
        name: '',
        slug: '',
        category: 'Restaurant',
        description: '',
        thumbnail_url: 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=600',
        is_premium: false,
        sort_order: 0
    });
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    // Code Editor states & helpers
    const [showCodeModal, setShowCodeModal] = useState(false);
    const [htmlInput, setHtmlInput] = useState('');
    const [cssInput, setCssInput] = useState('');

    const handleOpenCodeModal = () => {
        if (!editor) return;
        setHtmlInput(editor.getHtml() || '');
        setCssInput(editor.getCss() || '');
        setShowCodeModal(true);
    };

    const handleSaveCode = (e) => {
        if (e) e.preventDefault();
        if (!editor) return;

        // Extract style tags from the HTML content
        const styleRegex = /<style[^>]*>([\s\S]*?)<\/style>/gi;
        let match;
        let extractedCss = '';
        while ((match = styleRegex.exec(htmlInput)) !== null) {
            extractedCss += match[1] + '\n';
        }

        // Try to parse out the body content if present
        const bodyRegex = /<body[^>]*>([\s\S]*?)<\/body>/i;
        const bodyMatch = bodyRegex.exec(htmlInput);
        let cleanHtml = bodyMatch ? bodyMatch[1] : htmlInput;

        // Remove style blocks from cleanHtml to avoid double injection
        cleanHtml = cleanHtml.replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '');

        // Apply HTML structure and styles separately to GrapesJS
        editor.setComponents(cleanHtml);
        editor.setStyle(extractedCss + (cssInput || ''));

        setShowCodeModal(false);
        toast.success('Template layout updated from custom code block!');
    };

    useEffect(() => {
        const loadTemplate = async () => {
            if (!id) {
                // Create Mode: Init editor with blank canvas
                initEditor(null);
                return;
            }
            setLoading(true);
            try {
                const res = await api.get(`/api/sites/templates/${id}`);
                const data = res.data;
                setTemplateData({
                    name: data.name || '',
                    slug: data.slug || '',
                    category: data.category || 'Restaurant',
                    description: data.description || '',
                    thumbnail_url: data.thumbnail_url || '',
                    is_premium: !!data.is_premium,
                    sort_order: data.sort_order || 0
                });
                initEditor(data);
            } catch (err) {
                console.error('Failed to load template details', err);
                toast.error('Failed to load template details.');
                navigate('/super-admin');
            } finally {
                setLoading(false);
            }
        };

        loadTemplate();

        return () => {
            if (editor) {
                editor.destroy();
            }
        };
    }, [id]);

    const initEditor = (initialData) => {
        if (editor) return;

        const e = grapesjs.init({
            container: editorRef.current,
            height: '100%',
            width: '100%',
            storageManager: false,
            plugins: ['gjs-preset-webpage'],
            canvas: {
                styles: [
                    'https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap',
                    'https://cdn.tailwindcss.com'
                ]
            }
        });

        // Register default blocks for building templates
        const bm = e.BlockManager;

        bm.add('hero-section', {
            label: 'Hero Section',
            category: 'Restaurant',
            content: `
                <section style="position: relative; padding: 120px 20px; text-align: center; color: white; overflow: hidden; min-height: 450px; display: flex; align-items: center; justify-content: center; width: 100%;">
                    <img src="https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=1200" alt="Hero Background Image" style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; object-fit: cover; z-index: 0;" />
                    <div style="position: absolute; top: 0; left: 0; right: 0; bottom: 0; background-color: rgba(0, 0, 0, 0.55); z-index: 1; pointer-events: none;"></div>
                    <div style="position: relative; z-index: 2; max-width: 800px; margin: 0 auto;">
                        <h1 style="font-size: 3.5rem; margin-bottom: 20px; font-weight: 700; font-family: 'Inter', sans-serif;">Welcome to Our Restaurant</h1>
                        <p style="font-size: 1.25rem; margin-bottom: 30px; font-family: 'Inter', sans-serif;">Fresh ingredients, craft recipes, and memorable culinary moments.</p>
                        <a href="#menu" style="display: inline-block; padding: 12px 30px; background-color: #f97316; color: white; text-decoration: none; border-radius: 6px; font-weight: 600; font-family: 'Inter', sans-serif;">Explore Menu</a>
                    </div>
                </section>
            `
        });

        bm.add('menu-section', {
            label: 'Menu Grid',
            category: 'Restaurant',
            content: `
                <section id="menu" style="padding: 60px 20px; width: 100%; font-family: 'Inter', sans-serif; background: #fafaf9;">
                    <div style="max-width: 1200px; margin: 0 auto;">
                        <h2 style="text-align: center; margin-bottom: 40px; font-size: 2.5rem; font-weight: 700; color: #1c1917;">Our Menu</h2>
                        <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); gap: 30px;">
                            <div style="border: 1px solid #eee; padding: 20px; border-radius: 12px; background: white; box-shadow: 0 4px 6px rgba(0,0,0,0.02);">
                                <img src="https://images.unsplash.com/photo-1513104890138-7c749659a591?w=400" alt="Pizza" style="width: 100%; height: 180px; object-fit: cover; border-radius: 8px; margin-bottom: 15px;" />
                                <h3 style="margin: 0 0 10px 0; font-size: 1.25rem; font-weight: 600;">Signature Pizza</h3>
                                <p style="color: #666; margin-bottom: 15px; font-size: 0.9rem; line-height: 1.4;">Fresh tomatoes, sliced mozzarella, sweet basil, and extra virgin olive oil.</p>
                                <div style="font-weight: bold; color: #f97316; font-size: 1.15rem;">₹299</div>
                            </div>
                        </div>
                    </div>
                </section>
            `
        });

        if (initialData && initialData.page_data) {
            try {
                const projectData = typeof initialData.page_data === 'string'
                    ? JSON.parse(initialData.page_data)
                    : initialData.page_data;
                e.loadProjectData(projectData);
            } catch (err) {
                console.error("Error parsing page data:", err);
            }
        }

        setEditor(e);
    };

    const handleSave = async (e) => {
        if (e) e.preventDefault();
        if (!editor) return;
        if (!templateData.name || !templateData.slug) {
            toast.error('Please enter a Template Name and URL Slug.');
            return;
        }

        setSaving(true);
        try {
            const projectData = editor.getProjectData();
            const payload = {
                ...templateData,
                page_data: projectData
            };

            if (id) {
                await api.put(`/api/sites/admin/templates/${id}`, payload);
                toast.success('Template updated successfully!');
            } else {
                await api.post('/api/sites/admin/templates', payload);
                toast.success('New Template created successfully!');
                navigate('/super-admin');
            }
        } catch (err) {
            console.error('Template save error', err);
            toast.error(err.response?.data?.error || 'Failed to save template.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="h-screen flex flex-col overflow-hidden bg-slate-900 relative">
            {/* Loading Overlay */}
            {loading && (
                <div className="absolute inset-0 bg-slate-950/90 flex items-center justify-center text-slate-300 z-50">
                    <div className="text-center font-black animate-pulse">LOADING EDITOR CONFIGURATION...</div>
                </div>
            )}
            {/* Custom Admin Metadata Header */}
            <div className="bg-slate-950 border-b border-slate-800 p-4 flex flex-col md:flex-row md:items-center justify-between gap-4 shrink-0">
                <div className="flex items-center gap-4">
                    <button onClick={() => navigate('/super-admin')} className="text-slate-400 hover:text-white transition-colors">
                        <ArrowLeft size={20} />
                    </button>
                    <div>
                        <h2 className="text-sm font-black text-white uppercase tracking-widest">Template Designer Terminal</h2>
                        <p className="text-xs text-slate-500 font-medium">Design professional web layouts for all site builder users.</p>
                    </div>
                </div>

                <form onSubmit={handleSave} className="flex flex-wrap items-center gap-3">
                    <input
                        type="text"
                        required
                        placeholder="Template Name"
                        className="bg-slate-900 border-slate-800 rounded-xl px-3 py-1.5 text-xs text-white focus:ring-1 focus:ring-amber-500 font-bold"
                        value={templateData.name}
                        onChange={e => setTemplateData({...templateData, name: e.target.value})}
                    />
                    <input
                        type="text"
                        required
                        placeholder="slug (e.g. coffee-hub)"
                        className="bg-slate-900 border-slate-800 rounded-xl px-3 py-1.5 text-xs text-white focus:ring-1 focus:ring-amber-500 font-mono"
                        value={templateData.slug}
                        onChange={e => setTemplateData({...templateData, slug: e.target.value})}
                    />
                    <select
                        className="bg-slate-900 border-slate-800 rounded-xl px-3 py-1.5 text-xs text-white focus:ring-1 focus:ring-amber-500 font-bold"
                        value={templateData.category}
                        onChange={e => setTemplateData({...templateData, category: e.target.value})}
                    >
                        <option value="Restaurant">Restaurant</option>
                        <option value="Cafe">Cafe</option>
                        <option value="Catering">Catering</option>
                        <option value="Cloud Kitchen">Cloud Kitchen</option>
                    </select>

                    <label className="flex items-center text-xs text-slate-300 font-semibold gap-1.5 cursor-pointer">
                        <input
                            type="checkbox"
                            className="rounded border-slate-800 text-amber-500 focus:ring-0 bg-slate-900"
                            checked={templateData.is_premium}
                            onChange={e => setTemplateData({...templateData, is_premium: e.target.checked})}
                        />
                        <span>Premium</span>
                    </label>

                    <div className="h-6 w-px bg-slate-800 mx-1"></div>

                    <button
                        type="submit"
                        disabled={saving}
                        className="flex items-center gap-2 px-4 py-1.5 bg-amber-500 hover:bg-amber-600 text-gray-900 rounded-xl text-xs font-black uppercase tracking-wider transition-all disabled:opacity-50"
                    >
                        <Save size={14} />
                        {saving ? 'Saving...' : 'Save Template'}
                    </button>
                </form>
            </div>

            {/* Custom Toolbar */}
            <div className="h-10 bg-slate-950 border-b border-slate-900 flex items-center justify-end px-4 gap-2 shrink-0">
                <button 
                    onClick={() => editor && editor.runCommand('set-device-desktop')}
                    className="p-1 text-slate-400 hover:text-white rounded" title="Desktop"
                >
                    <Monitor size={16} />
                </button>
                <button 
                    onClick={() => editor && editor.runCommand('set-device-mobile')}
                    className="p-1 text-slate-400 hover:text-white rounded" title="Mobile"
                >
                    <Smartphone size={16} />
                </button>

                <div className="h-4 w-px bg-slate-800 mx-1"></div>

                <button 
                    onClick={handleOpenCodeModal}
                    className="flex items-center gap-1.5 px-2.5 py-1 text-slate-400 hover:text-white hover:bg-slate-900 rounded transition-colors" 
                    title="Edit HTML & CSS Code"
                >
                    <Code size={16} />
                    <span className="text-xs font-bold font-mono">&lt;&gt; Code</span>
                </button>
            </div>

            {/* GrapesJS Container */}
            <div className="flex-1 relative">
                <div ref={editorRef} className="h-full w-full"></div>
            </div>
            
            <style dangerouslySetInnerHTML={{__html: `
                .gjs-one-bg { background-color: #0f172a; }
                .gjs-two-color { color: #cbd5e1; }
                .gjs-three-bg { background-color: #1e293b; color: white; }
                .gjs-four-color, .gjs-four-color-h:hover { color: #f59e0b; }
                .gjs-pn-panel { background-color: #0f172a; border-color: #1e293b; }
                .gjs-cv-canvas { background-color: #cbd5e1; }
                .gjs-block { background-color: #1e293b; border: 1px solid #334155; color: #cbd5e1; border-radius: 8px; }
                .gjs-block:hover { color: #f59e0b; border-color: #f59e0b; }
            `}} />

            {/* Raw HTML / CSS Code Editor Modal */}
            {showCodeModal && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-950/80 backdrop-blur-sm p-4">
                    <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 w-full max-w-4xl shadow-2xl flex flex-col max-h-[85vh] animate-scale-in text-left">
                        <div className="flex items-center justify-between pb-4 border-b border-slate-800 mb-4 shrink-0">
                            <div>
                                <h3 className="text-lg font-black text-white flex items-center gap-2">
                                    <Code className="text-amber-500" /> HTML & CSS Code Editor
                                </h3>
                                <p className="text-xs text-slate-500 mt-1">Directly edit raw layout markup and custom stylesheets.</p>
                            </div>
                            <button 
                                onClick={() => setShowCodeModal(false)}
                                className="text-slate-400 hover:text-white font-bold text-sm"
                            >
                                Close
                            </button>
                        </div>

                        <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4 min-h-0 overflow-y-auto mb-6">
                            <div className="flex flex-col h-full">
                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2 font-mono">&lt;HTML components&gt;</label>
                                <textarea
                                    className="flex-1 w-full bg-slate-950 border border-slate-800 rounded-xl p-4 text-xs font-mono text-emerald-400 focus:ring-1 focus:ring-amber-500 resize-none h-[40vh] md:h-auto"
                                    value={htmlInput}
                                    onChange={e => setHtmlInput(e.target.value)}
                                    placeholder="Write or paste raw HTML markup here..."
                                />
                            </div>
                            <div className="flex flex-col h-full">
                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2 font-mono">/* CSS stylesheet */</label>
                                <textarea
                                    className="flex-1 w-full bg-slate-950 border border-slate-800 rounded-xl p-4 text-xs font-mono text-amber-400 focus:ring-1 focus:ring-amber-500 resize-none h-[40vh] md:h-auto"
                                    value={cssInput}
                                    onChange={e => setCssInput(e.target.value)}
                                    placeholder="Write or paste custom styles here..."
                                />
                            </div>
                        </div>

                        <div className="flex justify-end gap-3 shrink-0 pt-4 border-t border-slate-800">
                            <button
                                type="button"
                                onClick={() => setShowCodeModal(false)}
                                className="px-6 py-2.5 rounded-xl border border-slate-800 text-slate-400 hover:bg-slate-800 text-xs font-bold transition-all"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleSaveCode}
                                className="bg-amber-500 hover:bg-amber-600 text-gray-900 font-black uppercase tracking-wider text-xs py-2.5 px-8 rounded-xl shadow-lg transition-all"
                            >
                                Save Code & Apply
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
